import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../cn';
import { useTimezone } from '../timezone/TimezoneContext';
import { DataTableShell, SortableTableHead, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../data-table';
import { balanceAfterNet, formatUsdAmount, formatUsdCurrency, ledgerDisplayStatus, signedNetAmount, usdLedgerCreatedAtCompact, usdLedgerPaymentMethodLabel, usdLedgerSourceLabel, usdLedgerStatusBadgeClass, usdLedgerStatusLabel, usdLedgerTypeLabel, usdPartyDisplayLabel, } from './ledgerUtils';
import { usdLedgerActivityLabel } from './usdLedgerActivityLabel';
import { usdLedgerReferenceLabel } from './usdLedgerReferenceLabel';
import { CopyTextButton } from '../portal-ledger/CopyTextButton';
// Desktop table columns: Owner | Activity | Type | Status | Balance before | Balance after | Reference | Amount | Created
const COL_WIDTHS = ['13%', '15%', '8%', '10%', '12%', '12%', '14%', '8%', '8%'];
const PAYMENT_CELL_CLASS = 'px-3 py-3 align-top text-center';
const TABLE_HEAD_BASE = 'h-10 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500';
const TABLE_HEAD_LEFT = cn(TABLE_HEAD_BASE, 'text-left');
const TABLE_HEAD_RIGHT = cn(TABLE_HEAD_BASE, 'text-right');
function FraudWarningIcon({ row, onOpenFraudIndicator, }) {
    var _a;
    const fraudId = (_a = row.fraudFlag) === null || _a === void 0 ? void 0 : _a.id;
    const clickable = Boolean(fraudId && onOpenFraudIndicator);
    const icon = (React.createElement(AlertTriangle, { className: cn('h-3.5 w-3.5 shrink-0 text-amber-600', clickable && 'cursor-pointer hover:text-amber-700'), "aria-hidden": true }));
    if (clickable) {
        return (React.createElement("button", { type: "button", onClick: (e) => {
                e.stopPropagation();
                onOpenFraudIndicator(fraudId);
            }, className: "inline-flex shrink-0 rounded p-0.5 hover:bg-amber-50", "aria-label": "Flagged by fraud indicator", title: "Flagged by fraud indicator" }, icon));
    }
    return (React.createElement("span", { title: "Flagged by fraud indicator", "aria-label": "Flagged by fraud indicator" }, icon));
}
function truncateMonoId(txId, fallbackId) {
    const full = txId || fallbackId || '—';
    const display = full.length > 12 && txId ? `${txId.slice(0, 8)}…` : full;
    return { display, full };
}
function TransactionBlock({ row, onOpenFraudIndicator, }) {
    var _a, _b, _c, _d;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    const ledgerId = String((_c = row.id) !== null && _c !== void 0 ? _c : '').trim();
    const { display, full } = truncateMonoId(txId, ledgerId);
    const copyText = txId || ledgerId;
    const paymentLabel = usdLedgerPaymentMethodLabel(row);
    const sourceLabel = usdLedgerSourceLabel(row);
    const fraudFlagged = Boolean((_d = row.fraudFlagged) !== null && _d !== void 0 ? _d : row.fraudFlag);
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
            fraudFlagged ? React.createElement(FraudWarningIcon, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }) : null,
            React.createElement("span", { className: "min-w-0 truncate font-mono text-xs font-medium text-gray-800", title: full !== '—' ? full : undefined }, display),
            copyText ? React.createElement(CopyTextButton, { text: copyText, label: "Copy transaction ID" }) : null),
        React.createElement("p", { className: "mt-1 truncate text-xs text-gray-600", title: paymentLabel !== '—' ? paymentLabel : undefined }, paymentLabel),
        sourceLabel ? (React.createElement("p", { className: "mt-0.5 truncate text-[11px] text-gray-500", title: sourceLabel }, sourceLabel)) : null));
}
function TransactionIdColumnBlock({ row, onOpenFraudIndicator, }) {
    var _a, _b, _c, _d;
    const txId = String((_b = (_a = row.transactionId) !== null && _a !== void 0 ? _a : row.transaction_id) !== null && _b !== void 0 ? _b : '').trim();
    const ledgerId = String((_c = row.id) !== null && _c !== void 0 ? _c : '').trim();
    const { display, full } = truncateMonoId(txId, ledgerId);
    const copyText = txId || ledgerId;
    const fraudFlagged = Boolean((_d = row.fraudFlagged) !== null && _d !== void 0 ? _d : row.fraudFlag);
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("div", { className: "flex min-w-0 items-center gap-1" },
            fraudFlagged ? React.createElement(FraudWarningIcon, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }) : null,
            React.createElement("span", { className: "min-w-0 truncate font-mono text-xs font-medium text-gray-800", title: full !== '—' ? full : undefined }, display),
            copyText ? React.createElement(CopyTextButton, { text: copyText, label: "Copy transaction ID" }) : null)));
}
function PaymentColumnBlock({ row }) {
    const paymentLabel = usdLedgerPaymentMethodLabel(row);
    return (React.createElement("p", { className: "break-words text-sm leading-snug text-gray-700" }, paymentLabel));
}
function PartiesBlock({ row }) {
    const from = usdPartyDisplayLabel(row.fromParty, row, 'from');
    const to = usdPartyDisplayLabel(row.toParty, row, 'to');
    return (React.createElement("div", { className: "min-w-0 space-y-1 text-sm leading-snug" },
        React.createElement("p", { className: "truncate whitespace-nowrap text-gray-900", title: from }, from),
        React.createElement("p", { className: "truncate whitespace-nowrap text-gray-900", title: to }, to)));
}
function ActivityColumnBlock({ row }) {
    const label = usdLedgerActivityLabel(row);
    return (React.createElement("p", { className: "text-sm leading-snug text-gray-800", title: label }, label));
}
function OwnerColumnBlock({ row }) {
    var _a, _b, _c, _d;
    const type = String((_a = row.type) !== null && _a !== void 0 ? _a : '').toLowerCase();
    const signed = signedNetAmount(row);
    const isOutflow = type === 'withdrawal' || type === 'purchase_credits' || signed < -1e-9;
    const isInflow = type === 'deposit' || signed > 1e-9;
    const ownerParty = isOutflow
        ? (_b = row.fromParty) !== null && _b !== void 0 ? _b : row.toParty
        : isInflow
            ? (_c = row.toParty) !== null && _c !== void 0 ? _c : row.fromParty
            : (_d = row.fromParty) !== null && _d !== void 0 ? _d : row.toParty;
    const owner = usdPartyDisplayLabel(ownerParty, row, isOutflow ? 'from' : 'to');
    return (React.createElement("p", { className: "truncate whitespace-nowrap text-sm leading-snug text-gray-900", title: owner }, owner));
}
function PartyColumnBlock({ label, value, }) {
    return (React.createElement("div", { className: "min-w-0" },
        React.createElement("p", { className: "truncate whitespace-nowrap text-sm leading-snug text-gray-900", title: `${label}: ${value}` }, value)));
}
function FromPartyBlock({ row }) {
    return React.createElement(PartyColumnBlock, { label: "From", value: usdPartyDisplayLabel(row.fromParty, row, 'from') });
}
function ToPartyBlock({ row }) {
    return React.createElement(PartyColumnBlock, { label: "To", value: usdPartyDisplayLabel(row.toParty, row, 'to') });
}
function StatusBlock({ row }) {
    const typeLabel = usdLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    const st = ledgerDisplayStatus(row);
    const statusLabel = usdLedgerStatusLabel(st);
    return (React.createElement("div", { className: "flex min-w-0 flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center" },
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700') }, typeLabel),
        React.createElement("span", { className: cn('inline-flex w-fit shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold capitalize', usdLedgerStatusBadgeClass(st)) }, statusLabel)));
}
function TypeBadgeColumnBlock({ row }) {
    const typeLabel = usdLedgerTypeLabel(row);
    const isDeposit = typeLabel === 'Deposit';
    const isWithdraw = typeLabel === 'Withdraw';
    return (React.createElement("span", { className: cn('inline-flex max-w-full rounded-md px-2 py-0.5 text-xs font-semibold leading-snug', isDeposit && 'bg-emerald-100 text-emerald-800', isWithdraw && 'bg-red-100 text-red-800', !isDeposit && !isWithdraw && 'bg-gray-100 text-gray-700'), title: typeLabel }, typeLabel));
}
function StatusBadgeColumnBlock({ row }) {
    const st = ledgerDisplayStatus(row);
    const statusLabel = usdLedgerStatusLabel(st);
    return (React.createElement("span", { className: cn('inline-flex w-fit rounded-md px-2 py-0.5 text-xs font-semibold capitalize', usdLedgerStatusBadgeClass(st)) }, statusLabel));
}
function AmountBlock({ row }) {
    const signed = signedNetAmount(row);
    return (React.createElement("span", { className: cn('block text-right font-semibold tabular-nums', signed > 1e-9 ? 'text-emerald-700' : signed < -1e-9 ? 'text-red-700' : 'text-gray-700') }, formatUsdAmount(signed)));
}
function BalanceBeforeBlock({ row }) {
    var _a, _b;
    const before = Number((_b = (_a = row.balanceNetBefore) !== null && _a !== void 0 ? _a : row.balance_net_before) !== null && _b !== void 0 ? _b : 0);
    return React.createElement("span", { className: "block text-right tabular-nums text-gray-800" }, formatUsdCurrency(before));
}
function BalanceBlock({ row }) {
    return (React.createElement("span", { className: "block text-right tabular-nums text-gray-800" }, formatUsdCurrency(balanceAfterNet(row))));
}
function ReferenceBlock({ row }) {
    const { primary, secondary, title } = usdLedgerReferenceLabel(row);
    return (React.createElement("span", { className: "block min-w-0 text-xs leading-snug text-gray-700", title: title },
        React.createElement("span", { className: "block truncate font-semibold text-gray-800" }, primary),
        secondary ? (React.createElement("span", { className: "block truncate font-mono text-[11px] text-gray-500" }, secondary)) : null));
}
function CreatedBlock({ row, timezone }) {
    const created = usdLedgerCreatedAtCompact(row, timezone);
    if (!created)
        return React.createElement("span", { className: "block text-right text-gray-500" }, "\u2014");
    return (React.createElement("div", { className: "whitespace-nowrap text-right text-xs leading-snug text-gray-600" },
        React.createElement("div", null, created.dateLine),
        React.createElement("div", { className: "text-gray-500" }, created.timeLine)));
}
function MobileUsdLedgerCard({ row, onViewDetail, onOpenFraudIndicator, timezone }) {
    const openDetail = onViewDetail ? () => onViewDetail(row) : undefined;
    return (React.createElement("article", { className: cn('space-y-3 border-b border-gray-100 px-4 py-4 last:border-b-0', openDetail && 'cursor-pointer hover:bg-gray-50/80'), onClick: openDetail, onKeyDown: openDetail
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openDetail();
                }
            }
            : undefined, role: openDetail ? 'button' : undefined, tabIndex: openDetail ? 0 : undefined },
        React.createElement("div", { className: "flex items-start justify-between gap-3" },
            React.createElement("div", { className: "min-w-0 flex-1" },
                React.createElement(TransactionBlock, { row: row, onOpenFraudIndicator: onOpenFraudIndicator }))),
        React.createElement(PartiesBlock, { row: row }),
        React.createElement("div", null,
            React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Activity"),
            React.createElement(ActivityColumnBlock, { row: row })),
        React.createElement("div", { className: "flex flex-wrap items-center justify-between gap-3" },
            React.createElement(StatusBlock, { row: row }),
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Amount"),
                React.createElement(AmountBlock, { row: row }))),
        React.createElement("div", { className: "flex items-end justify-between gap-4" },
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Balance before"),
                React.createElement(BalanceBeforeBlock, { row: row })),
            React.createElement("div", null,
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Balance after"),
                React.createElement(BalanceBlock, { row: row })),
            React.createElement("div", { className: "text-right" },
                React.createElement("p", { className: "text-[11px] font-medium uppercase tracking-wide text-gray-500" }, "Created"),
                React.createElement(CreatedBlock, { row: row, timezone: timezone })))));
}
export const PortalUsdWalletLedgerTable = ({ rows, loading, isGloballyEmpty, className, onViewDetail, onOpenFraudIndicator, sortable = false, sortActiveKey = null, sortDirection = null, onSort, }) => {
    const { timezone } = useTimezone();
    const sortHead = (key, label, align = 'left', magnitude = false) => {
        if (!sortable || !onSort) {
            return (React.createElement(TableHead, { className: align === 'right' ? TABLE_HEAD_RIGHT : TABLE_HEAD_LEFT }, label));
        }
        return (React.createElement(SortableTableHead, { sortable: true, label: label, sortKey: key, activeKey: sortActiveKey, direction: sortDirection, onSort: onSort, align: align, magnitude: magnitude }));
    };
    if (loading) {
        return (React.createElement("div", { className: cn('px-4 py-12 text-center text-sm text-gray-500', className) }, "Loading USD transactions\u2026"));
    }
    if (rows.length === 0) {
        return (React.createElement("div", { className: cn('px-4 py-14 text-center', className) },
            React.createElement("p", { className: "text-sm font-medium text-gray-900" }, "No transactions found"),
            React.createElement("p", { className: "mt-1 text-sm text-gray-500" }, isGloballyEmpty
                ? 'Your ledger will appear after deposits, withdrawals, or adjustments.'
                : 'Try changing your filters or search keyword.')));
    }
    return (React.createElement(DataTableShell, { borderless: true, className: className },
        React.createElement("div", { className: "md:hidden" }, rows.map((r) => (React.createElement(MobileUsdLedgerCard, { key: String(r.id), row: r, onViewDetail: onViewDetail, onOpenFraudIndicator: onOpenFraudIndicator, timezone: timezone })))),
        React.createElement("div", { className: "hidden md:block" },
            React.createElement(Table, { className: "table-fixed w-full" },
                React.createElement("colgroup", null, COL_WIDTHS.map((w) => (React.createElement("col", { key: w, style: { width: w } })))),
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, { className: "border-b border-gray-100 bg-gray-50/90 hover:bg-gray-50/90" },
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Owner"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Activity"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Type"),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Status"),
                        sortHead('balanceBefore', 'Balance before', 'right'),
                        sortHead('balanceAfter', 'Balance after', 'right'),
                        React.createElement(TableHead, { className: TABLE_HEAD_LEFT }, "Reference"),
                        sortHead('amount', 'Amount', 'right', true),
                        sortHead('createdAt', 'Created', 'right'))),
                React.createElement(TableBody, null, rows.map((r) => {
                    const openDetail = onViewDetail ? () => onViewDetail(r) : undefined;
                    return (React.createElement(TableRow, { key: String(r.id), className: cn('border-gray-100', openDetail && 'cursor-pointer hover:bg-gray-50/80'), onClick: openDetail, onKeyDown: openDetail
                            ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openDetail();
                                }
                            }
                            : undefined, tabIndex: openDetail ? 0 : undefined, role: openDetail ? 'button' : undefined },
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(OwnerColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(ActivityColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(TypeBadgeColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(StatusBadgeColumnBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(BalanceBeforeBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(BalanceBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(ReferenceBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(AmountBlock, { row: r })),
                        React.createElement(TableCell, { className: "px-4 py-3 align-top" },
                            React.createElement(CreatedBlock, { row: r, timezone: timezone }))));
                }))))));
};
export default PortalUsdWalletLedgerTable;
